<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type {
  ChannelCategory,
  ProviderCode,
  PushApp,
  PushChannelConfig,
  PushChannelTestResult
} from './types'
import { CHANNEL_CATEGORIES, PROVIDER_CATALOG, PUSH_PROVIDERS, pushAppOptions } from './types'
import { formatTime, statusText } from './usePushCenterState'
import {
  AlertCircle,
  Apple,
  Layers,
  MessageSquare,
  Plus,
  Radio,
  RefreshCw,
  Sliders,
  Terminal
} from '@lucide/vue'
import {
  AdminDataTable,
  AdminFilterBar,
  AdminMetricStrip,
  AdminStatusTag,
  RrButton,
  RrSelect,
  RrSwitch,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'
import PushAppCreateDialog from './PushAppCreateDialog.vue'
import PushChannelCredentialDialog, {
  type ChannelCredentialFields
} from './PushChannelCredentialDialog.vue'
import PushChannelTestBanner from './PushChannelTestBanner.vue'

const props = withDefaults(
  defineProps<{
    apps?: PushApp[]
    configs?: PushChannelConfig[]
    total?: number
    pageNo?: number
    pageSize?: number
    appCode?: string
    selectedAppName?: string
    canManage?: boolean
    createApiClient: () => ApiClient
    testChannel?: (payload: {
      appCode: string
      provider: ProviderCode
      environment?: string
      credential?: Record<string, unknown>
    }) => Promise<PushChannelTestResult>
    toggleChannel?: (
      appCode: string,
      provider: ProviderCode,
      enabled: boolean,
      environment?: string
    ) => Promise<void>
  }>(),
  {
    apps: () => [],
    configs: () => [],
    appCode: '',
    selectedAppName: '',
    canManage: false,
    total: 0,
    pageNo: 1,
    pageSize: 10,
    testChannel: undefined,
    toggleChannel: undefined
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  refresh: []
  'page-change': [pageNo: number, pageSize: number]
  notice: [msg: string]
  error: [msg: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  localAppCode.value = code
  emit('update:appCode', code)
}

const tableColumns: TableColumnType<PushChannelConfig>[] = [
  { key: 'appCode', title: '应用编码', width: 130 },
  { key: 'provider', title: '通道厂商', width: 160 },
  { key: 'environment', title: '运行环境', width: 120 },
  { key: 'enabled', title: '状态', width: 100 },
  { key: 'credential', title: '凭证配置 (脱敏)', minWidth: 220 },
  { key: 'updatedAt', title: '更新时间', width: 180 },
  { key: 'actions', title: '操作', width: 150, align: 'right', fixed: 'right' }
]

const activeCategory = ref<ChannelCategory>('all')

const currentAppConfigs = computed(() => {
  const current = localAppCode.value || props.appCode
  return (props.configs || []).filter((c) => !current || c.appCode === current)
})

const selectedEnvironment = ref('PROD')
const environmentOptions = computed(() => {
  const environments = new Set(['PROD', 'SANDBOX'])
  currentAppConfigs.value.forEach((config) => environments.add(config.environment))
  return [...environments].map((environment) => ({ label: environment, value: environment }))
})

const configMap = computed(() => {
  const map = new Map<ProviderCode, PushChannelConfig>()
  for (const cfg of currentAppConfigs.value.filter(
    (config) => config.environment === selectedEnvironment.value
  )) {
    map.set(cfg.provider, cfg)
  }
  return map
})

const metrics = computed(() => {
  const total = PUSH_PROVIDERS.length
  const configured = currentAppConfigs.value.length
  const enabled = currentAppConfigs.value.filter((c) => c.enabled).length
  return [
    {
      key: 'total',
      label: '接入平台应用',
      value: `${props.apps?.length ?? 0} 个`,
      tone: 'neutral' as const
    },
    { key: 'providers', label: '支持通道厂商', value: `${total} 种`, tone: 'neutral' as const },
    {
      key: 'configured',
      label: '当前已配置通道',
      value: `${configured} 个`,
      tone: configured > 0 ? ('primary' as const) : ('neutral' as const)
    },
    {
      key: 'enabled',
      label: '已启用通道',
      value: `${enabled} 个`,
      hint: enabled > 0 ? '通道已就绪' : '无就绪通道',
      tone: enabled > 0 ? ('success' as const) : ('warning' as const)
    }
  ]
})

const filteredProviders = computed(() => {
  return PUSH_PROVIDERS.filter((provider) => {
    if (activeCategory.value === 'all') return true
    const meta = PROVIDER_CATALOG[provider]
    return meta?.category === activeCategory.value
  })
})

function categoryCount(cat: ChannelCategory): number {
  if (cat === 'all') return PUSH_PROVIDERS.length
  return PUSH_PROVIDERS.filter((p) => PROVIDER_CATALOG[p]?.category === cat).length
}

// 连通性测试状态管理
const testingMap = ref<Record<string, boolean>>({})
const testResultMap = ref<Record<string, PushChannelTestResult | null>>({})

function channelKey(appCode: string, provider: ProviderCode, environment: string): string {
  return `${appCode}|${provider}|${environment}`
}

function testResult(provider: ProviderCode, environment = selectedEnvironment.value) {
  return testResultMap.value[channelKey(localAppCode.value || props.appCode, provider, environment)]
}

function isTesting(provider: ProviderCode, environment = selectedEnvironment.value): boolean {
  return (
    testingMap.value[channelKey(localAppCode.value || props.appCode, provider, environment)] ??
    false
  )
}

function clearTestResults() {
  testingMap.value = {}
  testResultMap.value = {}
}

watch([localAppCode, selectedEnvironment], clearTestResults)

async function runTest(
  provider: ProviderCode,
  environment = selectedEnvironment.value,
  customCredential?: Record<string, unknown>
): Promise<PushChannelTestResult> {
  const currentApp = localAppCode.value || props.appCode
  if (!currentApp) {
    const res: PushChannelTestResult = {
      success: false,
      message: '请先选择目标应用',
      latencyMs: 0,
      level: 'VALIDATION'
    }
    emit('error', res.message)
    return res
  }

  const key = channelKey(currentApp, provider, environment)
  testingMap.value[key] = true
  testResultMap.value[key] = null
  try {
    let result: PushChannelTestResult
    if (props.testChannel) {
      result = await props.testChannel({
        appCode: currentApp,
        provider,
        environment,
        credential: customCredential
      })
    } else {
      const api = props.createApiClient()
      result = await api.post<PushChannelTestResult>('/api/saas/push/channel-configs/test', {
        appCode: currentApp,
        provider,
        environment,
        credential: customCredential
      })
    }
    testResultMap.value[key] = result
    if (result.success) {
      const outcome = result.level === 'AUTHENTICATION' ? '鉴权/连通验证成功' : '配置校验通过'
      emit(
        'notice',
        `${PROVIDER_CATALOG[provider]?.label ?? provider} ${outcome} (${result.latencyMs}ms)`
      )
    } else {
      emit('error', result.message)
    }
    return result
  } catch (err) {
    const res: PushChannelTestResult = {
      success: false,
      message: err instanceof Error ? err.message : '通道测试异常',
      latencyMs: 0,
      level: 'AUTHENTICATION'
    }
    testResultMap.value[key] = res
    emit('error', res.message)
    return res
  } finally {
    testingMap.value[key] = false
  }
}

async function onToggle(provider: ProviderCode, environment: string, enabled: boolean) {
  const currentApp = localAppCode.value || props.appCode
  if (!currentApp || !props.canManage) return
  try {
    if (props.toggleChannel) {
      await props.toggleChannel(currentApp, provider, enabled, environment)
    } else {
      const api = props.createApiClient()
      await api.post(
        `/api/saas/push/channel-configs/toggle?appCode=${encodeURIComponent(currentApp)}&provider=${provider}&environment=${encodeURIComponent(environment)}&enabled=${enabled}`
      )
      emit(
        'notice',
        `${PROVIDER_CATALOG[provider]?.label ?? provider} 已${enabled ? '启用' : '停用'}`
      )
      emit('refresh')
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '状态切换失败')
    emit('refresh')
  }
}

// 结构化表单配置弹窗
const channelDialogVisible = ref(false)
const savingChannel = ref(false)
const channelForm = ref({
  appCode: '',
  provider: 'JPUSH' as ProviderCode,
  environment: 'PROD',
  enabled: true,
  fields: {} as ChannelCredentialFields
})

function openChannelDialog(provider: ProviderCode, existing = configMap.value.get(provider)) {
  const currentApp = localAppCode.value || props.appCode
  if (!currentApp) {
    emit('error', '请先选择目标应用')
    return
  }
  channelForm.value.appCode = currentApp
  channelForm.value.provider = provider
  channelForm.value.environment = existing?.environment ?? 'PROD'
  channelForm.value.enabled = existing?.enabled ?? true

  const fields: ChannelCredentialFields = {}
  const cred = (existing?.credentialMasked || {}) as Record<string, unknown>

  switch (provider) {
    case 'APNS':
      fields.teamId = String(cred.teamId ?? '')
      fields.keyId = String(cred.keyId ?? '')
      fields.bundleId = String(cred.bundleId ?? cred.topic ?? '')
      fields.privateKey = ''
      fields.sandbox = Boolean(cred.sandbox ?? false)
      break
    case 'JPUSH':
      fields.appKey = String(cred.appKey ?? '')
      fields.masterSecret = ''
      fields.apnsProduction =
        cred.apnsProduction !== undefined ? Boolean(cred.apnsProduction) : true
      break
    case 'WECHAT_MINIAPP_SUBSCRIBE':
    case 'WECHAT_MP_TEMPLATE':
      fields.appId = String(cred.appId ?? '')
      fields.appSecret = ''
      fields.apiBaseUrl = String(cred.apiBaseUrl ?? '')
      break
    case 'FCM':
      fields.projectId = String(cred.projectId ?? '')
      fields.serviceAccountJson = ''
      break
    case 'HUAWEI_PUSH':
      fields.appId = String(cred.appId ?? '')
      fields.clientId = String(cred.clientId ?? '')
      fields.clientSecret = ''
      fields.packageName = String(cred.packageName ?? '')
      break
    case 'HARMONY_PUSH':
      fields.appId = String(cred.appId ?? '')
      fields.clientId = String(cred.clientId ?? '')
      fields.clientSecret = ''
      fields.bundleName = String(cred.bundleName ?? '')
      break
    case 'XIAOMI_PUSH':
      fields.appSecret = ''
      fields.packageName = String(cred.packageName ?? '')
      break
    case 'OPPO_PUSH':
      fields.appKey = String(cred.appKey ?? '')
      fields.masterSecret = ''
      break
    case 'VIVO_PUSH':
      fields.appId = String(cred.appId ?? '')
      fields.appKey = String(cred.appKey ?? '')
      fields.appSecret = ''
      break
    case 'GORUSH':
      fields.baseUrl = String(cred.baseUrl ?? '')
      fields.token = ''
      break
    default:
      break
  }

  channelForm.value.fields = fields
  channelDialogVisible.value = true
}

async function testDialogChannel() {
  await runTest(channelForm.value.provider, channelForm.value.environment, channelForm.value.fields)
}

async function saveChannelConfig() {
  if (!props.canManage) return
  if (!channelForm.value.appCode) {
    emit('error', '请先选择目标应用')
    return
  }
  savingChannel.value = true
  try {
    const api = props.createApiClient()
    const payload = {
      appCode: channelForm.value.appCode,
      provider: channelForm.value.provider,
      environment: channelForm.value.environment,
      enabled: channelForm.value.enabled,
      credential: channelForm.value.fields
    }
    await api.post('/api/saas/push/channel-configs', payload)
    emit(
      'notice',
      `${PROVIDER_CATALOG[channelForm.value.provider]?.label ?? channelForm.value.provider} 通道配置已保存`
    )
    channelDialogVisible.value = false
    testResultMap.value[channelKey(payload.appCode, payload.provider, payload.environment)] = null
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '通道配置保存失败')
  } finally {
    savingChannel.value = false
  }
}

// 应用管理弹窗
const appDialogVisible = ref(false)
const savingApp = ref(false)
const appForm = ref({
  appCode: '',
  appName: '',
  bundleId: '',
  packageName: '',
  harmonyAppId: '',
  enabled: true
})

function openAppDialog() {
  appForm.value = {
    appCode: '',
    appName: '',
    bundleId: '',
    packageName: '',
    harmonyAppId: '',
    enabled: true
  }
  appDialogVisible.value = true
}

async function saveApp() {
  if (!props.canManage) return
  if (!appForm.value.appCode.trim() || !appForm.value.appName.trim()) {
    emit('error', '应用编码与应用名称不能为空')
    return
  }
  savingApp.value = true
  try {
    const api = props.createApiClient()
    await api.post('/api/saas/push/apps', appForm.value)
    emit('notice', `应用 ${appForm.value.appName} 创建成功`)
    appDialogVisible.value = false
    localAppCode.value = appForm.value.appCode
    emit('update:appCode', appForm.value.appCode)
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '应用创建失败')
  } finally {
    savingApp.value = false
  }
}

function providerLabel(code?: unknown): string {
  if (!code) return ''
  const p = String(code) as ProviderCode
  return PROVIDER_CATALOG[p]?.label ?? p
}

function getProviderIcon(provider: ProviderCode) {
  switch (provider) {
    case 'APNS':
      return Apple
    case 'WECHAT_MINIAPP_SUBSCRIBE':
    case 'WECHAT_MP_TEMPLATE':
      return MessageSquare
    case 'JPUSH':
    case 'GORUSH':
      return Layers
    case 'MOCK':
      return Terminal
    default:
      return Radio
  }
}
</script>

<template>
  <div class="push-channel-manager">
    <!-- 顶部概览指标条 -->
    <AdminMetricStrip :metrics="metrics" class="push-metrics" />

    <!-- 顶部控制与分类过滤栏 -->
    <AdminFilterBar :show-search="false" :show-reset="false" class="push-top-bar">
      <div class="filter-controls">
        <label class="app-selector-label">
          <span class="label-text">当前应用</span>
          <RrSelect
            v-model:value="localAppCode"
            :options="pushAppOptions(apps)"
            placeholder="请选择应用"
            class="app-select"
            @change="onAppChange"
          />
        </label>

        <label class="app-selector-label">
          <span class="label-text">配置环境</span>
          <RrSelect
            v-model:value="selectedEnvironment"
            :options="environmentOptions"
            class="app-select"
          />
        </label>

        <!-- 分类切换胶囊 -->
        <div class="category-tabs" role="tablist">
          <button
            v-for="cat in CHANNEL_CATEGORIES"
            :key="cat.key"
            type="button"
            class="category-tab-btn"
            :class="{ active: activeCategory === cat.key }"
            @click="activeCategory = cat.key"
          >
            {{ cat.label }}
            <span class="cat-badge">{{ categoryCount(cat.key) }}</span>
          </button>
        </div>
      </div>

      <template #actions>
        <RrButton type="primary" :disabled="!canManage" @click="openAppDialog">
          <template #icon><Plus :size="16" /></template>
          新建应用
        </RrButton>
      </template>
    </AdminFilterBar>

    <!-- 无接入应用提示横幅 -->
    <div v-if="!apps || apps.length === 0" class="empty-app-banner">
      <AlertCircle :size="18" class="banner-icon" />
      <div class="banner-text">
        <strong>当前尚未接入任何推送应用：</strong>
        请先点击右上角<strong>【+ 新建应用】</strong
        >创建平台移动客户端应用，然后再行配置下方各厂商推送通道。
      </div>
      <RrButton type="primary" size="small" :disabled="!canManage" @click="openAppDialog">
        立即创建应用
      </RrButton>
    </div>

    <!-- 通道卡片网格 -->
    <div class="channels-grid">
      <div
        v-for="provider in filteredProviders"
        :key="provider"
        class="channel-card"
        :class="[
          `accent-${PROVIDER_CATALOG[provider]?.accent ?? 'neutral'}`,
          {
            'is-configured': configMap.has(provider),
            'is-enabled': configMap.get(provider)?.enabled
          }
        ]"
      >
        <div class="channel-card-header">
          <div class="channel-identity">
            <div class="channel-icon-box">
              <component :is="getProviderIcon(provider)" :size="20" />
            </div>
            <div class="channel-title-wrap">
              <h3 class="channel-name">{{ PROVIDER_CATALOG[provider]?.label ?? provider }}</h3>
              <div class="channel-sub-tags">
                <span class="platform-badge">{{ PROVIDER_CATALOG[provider]?.platform }}</span>
                <span
                  v-if="configMap.has(provider)"
                  class="env-badge"
                  :class="configMap.get(provider)?.environment?.toLowerCase()"
                >
                  {{ configMap.get(provider)?.environment ?? 'PROD' }}
                </span>
              </div>
            </div>
          </div>

          <div class="channel-switch-wrap">
            <template v-if="configMap.has(provider)">
              <RrSwitch
                :checked="configMap.get(provider)?.enabled ?? false"
                :disabled="!canManage"
                @update:checked="(val) => onToggle(provider, selectedEnvironment, Boolean(val))"
              />
            </template>
            <template v-else>
              <span class="unconfigured-tag">未配置</span>
            </template>
          </div>
        </div>

        <p class="channel-desc">{{ PROVIDER_CATALOG[provider]?.description }}</p>

        <PushChannelTestBanner :result="testResult(provider)" />

        <div class="channel-card-footer">
          <div class="footer-info">
            <small v-if="configMap.has(provider)" class="time-tip">
              更新于 {{ formatTime(configMap.get(provider)?.updatedAt) }}
            </small>
            <small v-else class="time-tip muted">尚未配置凭证参数</small>
          </div>

          <div class="footer-actions">
            <RrButton
              v-if="configMap.has(provider)"
              size="small"
              class="test-btn"
              :disabled="isTesting(provider)"
              @click="runTest(provider, selectedEnvironment)"
            >
              <template #icon>
                <RefreshCw :size="13" :class="{ 'spin-icon': isTesting(provider) }" />
              </template>
              测试
            </RrButton>

            <RrButton
              size="small"
              :type="configMap.has(provider) ? 'default' : 'primary'"
              @click="openChannelDialog(provider)"
            >
              <template #icon><Sliders :size="13" /></template>
              {{ configMap.has(provider) ? '配置参数' : '立即配置' }}
            </RrButton>
          </div>
        </div>
      </div>
    </div>

    <!-- 已生效通道列表表格 -->
    <AdminDataTable
      title="已生效通道列表"
      :columns="tableColumns"
      :data-source="currentAppConfigs"
      :empty="currentAppConfigs.length === 0"
      empty-title="暂无已配置通道"
      empty-description="请在上方厂商卡片中点击【立即配置】完成通道凭证配置与启用。"
      :loading="false"
      :current="pageNo"
      :page-size="pageSize"
      :total="total"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="emit('refresh')"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'appCode'">
          <strong>{{ record.appCode }}</strong>
        </template>
        <template v-else-if="column.key === 'provider'">
          <RrTag tone="primary" :label="providerLabel(record.provider)" />
        </template>
        <template v-else-if="column.key === 'environment'">
          <span class="env-badge" :class="String(record.environment || '').toLowerCase()">
            {{ record.environment }}
          </span>
        </template>
        <template v-else-if="column.key === 'enabled'">
          <AdminStatusTag
            :text="statusText(record.enabled)"
            :tone="record.enabled ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'credential'">
          <code class="masked-code">{{ JSON.stringify(record.credentialMasked) }}</code>
        </template>
        <template v-else-if="column.key === 'updatedAt'">
          <span class="time-tip">{{ formatTime(record.updatedAt) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <div class="table-actions">
            <RrButton
              size="small"
              class="secondary"
              @click="openChannelDialog(record.provider as ProviderCode, record)"
            >
              配置
            </RrButton>
            <RrButton
              size="small"
              :disabled="isTesting(record.provider as ProviderCode, record.environment)"
              @click="runTest(record.provider as ProviderCode, record.environment)"
            >
              测试
            </RrButton>
          </div>
        </template>
      </template>
    </AdminDataTable>

    <PushChannelCredentialDialog
      v-model:open="channelDialogVisible"
      v-model:form="channelForm"
      :app-code="localAppCode || appCode"
      :saving="savingChannel"
      :testing="isTesting(channelForm.provider, channelForm.environment)"
      :test-result="testResult(channelForm.provider, channelForm.environment)"
      @save="saveChannelConfig"
      @test="testDialogChannel"
    />
    <PushAppCreateDialog
      v-model:open="appDialogVisible"
      v-model:form="appForm"
      :saving="savingApp"
      @save="saveApp"
    />
  </div>
</template>

<style scoped>
.push-channel-manager {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
  min-width: 0;
}
.push-metrics {
  margin-bottom: var(--rr-space-1);
}
.push-top-bar {
  background: var(--rr-color-surface);
  border-radius: var(--rr-radius-panel);
  border: 1px solid var(--rr-color-border);
  padding: var(--rr-space-2) var(--rr-space-4);
}
.filter-controls {
  display: flex;
  align-items: center;
  gap: var(--rr-space-6);
  flex-wrap: wrap;
}
.app-selector-label {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
  font-size: var(--rr-font-size-base);
  color: var(--rr-color-text);
  font-weight: var(--rr-font-weight-medium);
}
.app-select {
  min-width: 200px;
}
.category-tabs {
  display: flex;
  align-items: center;
  gap: var(--rr-space-1);
  background: var(--rr-color-canvas);
  padding: var(--rr-space-1);
  border-radius: var(--rr-radius-control);
}
.category-tab-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-2);
  padding: var(--rr-space-2) var(--rr-space-3);
  border: none;
  background: transparent;
  border-radius: var(--rr-radius-control);
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
  cursor: pointer;
  transition:
    color var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    background var(--rr-motion-duration-mid) var(--rr-motion-ease-out);
}
.category-tab-btn:hover {
  color: var(--rr-color-text);
}
.category-tab-btn.active {
  background: var(--rr-color-surface);
  color: var(--rr-color-primary);
  font-weight: var(--rr-font-weight-bold);
  box-shadow: var(--rr-shadow-1);
}
.cat-badge {
  font-size: var(--rr-font-size-xs);
  padding: 0 var(--rr-space-2);
  border-radius: var(--rr-radius-full);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}
.category-tab-btn.active .cat-badge {
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}
.channels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: var(--rr-space-4);
}
.channel-card {
  display: flex;
  flex-direction: column;
  min-height: 168px;
  background: var(--rr-color-surface);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  padding: var(--rr-space-4);
  box-shadow: var(--rr-shadow-1);
  transition:
    box-shadow var(--rr-motion-duration-mid) var(--rr-motion-ease-out),
    border-color var(--rr-motion-duration-mid) var(--rr-motion-ease-out);
}
.channel-card:hover {
  box-shadow: var(--rr-shadow-2);
  border-color: var(--rr-color-primary-border);
}
.channel-card.is-configured.is-enabled {
  border-top: var(--rr-space-1) solid var(--rr-color-primary);
}
.channel-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--rr-space-2);
  margin-bottom: var(--rr-space-3);
}
.channel-identity {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
}
.channel-icon-box {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--rr-space-8);
  height: var(--rr-space-8);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-primary-soft);
  color: var(--rr-color-primary);
}
.channel-title-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-1);
}
.channel-name {
  margin: 0;
  font-size: var(--rr-font-size-base);
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text);
}
.channel-sub-tags {
  display: flex;
  align-items: center;
  gap: var(--rr-space-1);
}
.platform-badge {
  font-size: var(--rr-font-size-xs);
  padding: 0 var(--rr-space-2);
  border-radius: var(--rr-radius-xs);
  background: var(--rr-color-surface-subtle);
  color: var(--rr-color-text-muted);
}
.env-badge {
  font-size: var(--rr-font-size-xs);
  font-weight: var(--rr-font-weight-bold);
  padding: 0 var(--rr-space-1);
  border-radius: var(--rr-radius-xs);
  text-transform: uppercase;
}
.env-badge.prod {
  background: var(--rr-color-success-soft);
  color: var(--rr-color-success);
}
.env-badge.sandbox {
  background: var(--rr-color-warning-soft);
  color: var(--rr-color-warning);
}
.channel-switch-wrap {
  display: flex;
  align-items: center;
}
.unconfigured-tag {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
  padding: var(--rr-space-1) var(--rr-space-2);
  background: var(--rr-color-surface-subtle);
  border-radius: var(--rr-radius-full);
}
.channel-desc {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
  line-height: var(--rr-line-height-base);
  margin: 0 0 var(--rr-space-3) 0;
  min-height: calc(var(--rr-font-size-sm) * var(--rr-line-height-base) * 2);
  flex: 1;
}
.channel-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--rr-color-border);
  padding-top: var(--rr-space-3);
  margin-top: auto;
}
.time-tip {
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
}
.footer-actions,
.table-actions {
  display: flex;
  align-items: center;
  gap: var(--rr-space-2);
}
.table-actions {
  justify-content: flex-end;
}
.masked-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text);
  background: var(--rr-color-surface-subtle);
  padding: var(--rr-space-1) var(--rr-space-2);
  border-radius: var(--rr-radius-xs);
}
.empty-app-banner {
  display: flex;
  align-items: center;
  gap: var(--rr-space-4);
  padding: var(--rr-space-3) var(--rr-space-4);
  background: var(--rr-color-primary-soft);
  border: 1px dashed var(--rr-color-primary-border);
  border-radius: var(--rr-radius-panel);
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-sm);
}
.empty-app-banner .banner-icon {
  color: var(--rr-color-primary);
  flex-shrink: 0;
}
.empty-app-banner .banner-text {
  flex: 1;
  line-height: var(--rr-line-height-base);
}
.spin-icon {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
